import { 
  AC_STEP_CONFIRM_AGE, 
  AC_STEP_PARENT_EMAIL, 
  AC_STEP_YOUTH_CONTACT, 
  AC_STEP_EMAIL_VERIFICATION_SENT 
} from "../../utils/AccountCreationConstants";
import { isUserUnderMinimumAge } from "./utils";

/**
 * Custom hook for handling account creation business logic
 * Manages flow control, step navigation, and decision making
 */
export const useAccountCreationFlow = ({ 
  accountCreationState, 
  updateAccountCreationStep,
  callInviteUser,
  postInviteUser 
}) => {

  /**
   * Determines the correct previous step based on user age and data
   * @param {Object} formState - Current form state
   */
  const navigateBack = (formState) => {
    if (accountCreationState.childData) {
      // Child account - return to main landing
      return { navigateToLanding: true };
    }

    const birthDate = accountCreationState.confirmAgeData?.birthDate;
    const isUnder16 = isUserUnderMinimumAge(birthDate);
    
    if (isUnder16 && accountCreationState.ParentPhoneNumberData) {
      // User is under 16 and has parent email data
      return {
        step: AC_STEP_PARENT_EMAIL,
        data: formState,
        dataKey: 'userInfoData'
      };
    }
    
    // Default: return to confirm age step
    return {
      step: AC_STEP_CONFIRM_AGE,
      data: formState,
      dataKey: 'userInfoData'
    };
  };

  /**
   * Determines the next step and handles transition
   * @param {Object} userInfoData - User information data
   */
  const proceedToNextStep = (userInfoData) => {
    if (isUserUnderMinimumAge(userInfoData.birthDate)) {
      // User is under 16 - go to youth contact step
      if(!userInfoData.youthEmail){
        updateAccountCreationStep(AC_STEP_YOUTH_CONTACT, userInfoData, 'userInfoData');
      }
      else{
        // If youth email is already provided, skip to email verification sent step
        updateAccountCreationStep(AC_STEP_EMAIL_VERIFICATION_SENT, userInfoData, 'userInfoData');
      }
    } else {
      // User is over 16 - go to email verification sent step
      updateAccountCreationStep(AC_STEP_EMAIL_VERIFICATION_SENT, userInfoData, 'userInfoData');
    }
  };

  /**
   * Handles the flow after CreateStep1 API response
   * @param {Object} response - API response from CreateStep1
   * @param {Object} userInfoData - User information data
   * @param {Function} onProceedToNext - Callback to proceed to next step
   * @param {boolean} ignoreMatches - Explicitly indicates if this is after user has clicked Link or Continue
   */
  const handleCreateStep1Response = (response, userInfoData, onProceedToNext, ignoreMatches = false) => {
    console.log('Processing CreateStep1 response:', response);
    console.log('Ignore matches (explicit):', ignoreMatches);

    // Check for error message first
    if (response?.objData?.errorMessage) {
      return { error: response.objData.errorMessage };
    }

    const matches = response?.objData?.matches || [];
    
    if (matches.length > 0) {
      return handleMatchesFound(matches, userInfoData, onProceedToNext, ignoreMatches);
    }
    
    // No matches - proceed based on age
    if (matches.length === 0) {
      return handleNoMatches(userInfoData, onProceedToNext);
    }
    
    // Handle API errors
    if (!response?.objData?.success) {
      return { 
        error: response?.objData?.errorMessage || response?.message || 'An error occurred during account creation.' 
      };
    }

    return { success: true };
  };

  /**
   * Handles the case where matches are found in CreateStep1 response
   * @param {Array} matches - Array of matched persons
   * @param {Object} userInfoData - User information data
   * @param {Function} onProceedToNext - Callback to proceed to next step
   * @param {boolean} ignoreMatches - Whether to ignore matches and proceed directly
   */
  const handleMatchesFound = (matches, userInfoData, onProceedToNext, ignoreMatches = false) => {
    const availableMatches = matches.filter(match => !match.isAlreadyLinked);
    
    console.log('Available matches after filtering already linked:', availableMatches);
    console.log('Ignore matches (handleMatchesFound):', ignoreMatches);

    // Proceed to InviteUser/next step
    if (ignoreMatches) {
      console.log('Ignore matches confirmed - proceeding to next step');
      return handleNoMatches(userInfoData, onProceedToNext);
    }

    // If all matches are already linked, let PersonDupPopupManager handle the display
    if (availableMatches.length === 0) {
      console.log('All matches are already linked - showing PersonDupPopupManager');
      return { showDupPopup: true };
    }

    // Single available match
    if (availableMatches.length === 1) {
      const match = availableMatches[0];
      console.log('Single available match found:', match);

      // If not all data matches, display an error to the user and do not auto-proceed, as they may be trying to link to the wrong account
      if (match.doesAllProvidedDataMatch === false) {
        console.log('Single available match but not all data matches - showing PersonDupPopupManager');
        return { 
          error: 'We found a potential match, but one or more fields do not match.  Please contact customer service.' 
        };        
        //return { showDupPopup: true };
      }
      
      // If all data matches, let PersonDupPopupManager handle the auto-selection and proceeding, as it will also handle the case where the matched account is a child account that cannot be linked to
      if (match.doesAllProvidedDataMatch === true) {
        console.log('Single available match and all data matches - proceeding with linking');
        return { showDupPopup: true };
      }
    }
    
    // Multiple available matches - show popup for selection
    console.log('Multiple available matches - showing PersonDupPopupManager for selection');
    return { showDupPopup: true };
  };

  /**
   * Handles the case where no matches are found
   * @param {Object} userInfoData - User information data  
   * @param {Function} onProceedToNext - Callback to proceed to next step
   */
  const handleNoMatches = (userInfoData, onProceedToNext) => {
    if (isUserUnderMinimumAge(userInfoData.birthDate)) {
      // User is under 16 - skip InviteUser and go directly to next step
      onProceedToNext();
      return { proceedDirectly: true };
    } else {
      // User is 16 or over - call InviteUser first
      return { callInviteUser: true };
    }
  };

  /**
   * Handles the InviteUser flow and proceeds to next step
   * @param {Object} userInfoData - User information data
   */
  const handleInviteUserAndProceed = async (userInfoData) => {
    const returnUrl = `${import.meta.env.VITE_YARP_BASE_URL}/bff/login?returnUrl=${import.meta.env.VITE_YARP_BASE_URL}/landing`;

    console.log('Inviting userInfoData :' + JSON.stringify(userInfoData, null, 2));
    try {
      await callInviteUser({
        email: userInfoData.email || userInfoData.youthEmail,
        firstName: userInfoData.firstName,
        lastName: userInfoData.lastName,
        returnUrl,
        postInviteUser,
        onSuccess: () => proceedToNextStep(userInfoData)
      });
    } catch (error) {
      // Error is already handled by callInviteUser
      console.error('Failed to invite user and proceed:', error);
    }
  };

  return {
    navigateBack,
    proceedToNextStep,
    handleCreateStep1Response,
    handleInviteUserAndProceed
  };
};